import {
	DownloadGroupName,
	DownloadGroups,
	DownloadGroupStatus,
	DownloadManagerItemState,
	DownloadManagerState,
	DownloadMode,
	DownloadStatus
} from '../containers/DownloadManager/reducer';
import { DownloadingAllStep, LoaderStep, LoaderStepName } from '../components/Loader';
import { DriveItemInfo, DriveItemInfoMap, DriveListState } from '../containers/DriveItemList/reducer';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import DriveItemUtils from './driveItem';
import { Api } from '../typings/api';

export default class DownloadManagerUtils {

	public static getDownloadedItems(downloadManager: DownloadManagerState): number[] {
		const downloadList = downloadManager.items;
		return Object.keys(downloadList)
			.map(id => parseInt(id, 10))
			.filter(id => DownloadManagerUtils.isItemDownloaded(downloadManager, id));
	}

	public static getDownloadingItems(downloadManager: DownloadManagerState): number[] {
		const downloadList = downloadManager.items;
		return Object.keys(downloadList)
			.map(id => parseInt(id, 10))
			.filter(id => DownloadManagerUtils.isItemDownloading(downloadManager, id));
	}

	public static getItemLoaderStep(downloadManager: DownloadManagerState, driveItemId: number): LoaderStep | undefined {
		if (DownloadManagerUtils.isItemDownloading(downloadManager, driveItemId)) {
			if (DownloadManagerUtils.isDownloadingAsset(downloadManager, driveItemId)) {
				const progress = downloadManager.items[driveItemId] && downloadManager.items[driveItemId].progress ? downloadManager.items[driveItemId].progress : {};

				return {
					step: LoaderStepName.Downloading,
					progress
				};
			}

			if (DownloadManagerUtils.isAssetDownloaded(downloadManager, driveItemId) && !DownloadManagerUtils.isDependenciesDownloaded(downloadManager, driveItemId)) {
				if (downloadManager.items[driveItemId]) {
					const totalDependenciesDownloaded = DownloadManagerUtils.getTotalDependenciesDownloaded(downloadManager, driveItemId);

					return {
						step: LoaderStepName.DownloadingDependencies,
						dependenciesDownloaded: totalDependenciesDownloaded,
						dependenciesToDownload: downloadManager.items[driveItemId].dependencies.length,
					};
				}
			}
		}

		return;
	}

	public static getStartedDownloadGroup(downloadGroups: DownloadGroups): DownloadGroupName | null {
		const {
			all: {
				status: downloadStatusAll,
			},
			important: {
				status: downloadStatusImportant,
			},
			selected: {
				status: downloadStatusSelected,
			}
		} = downloadGroups;
		if (downloadStatusAll === DownloadGroupStatus.Started) {
			return DownloadGroupName.ALL;
		}

		if (downloadStatusImportant === DownloadGroupStatus.Started) {
			return DownloadGroupName.IMPORTANT;
		}

		if (downloadStatusSelected === DownloadGroupStatus.Started) {
			return DownloadGroupName.SELECTED;
		}

		return null;
	}

	public static getDownloadingGroup(downloadGroups: DownloadGroups): DownloadGroupName | null {
		const {
			all: {
				isDownloading: isDownloadingAll,
			},
			important: {
				isDownloading: isDownloadingImportant,
			},
			selected: {
				isDownloading: isDownloadingSelected,
			}
		} = downloadGroups;

		if (isDownloadingAll) {
			return DownloadGroupName.ALL;
		}

		if (isDownloadingImportant) {
			return DownloadGroupName.IMPORTANT;
		}

		if (isDownloadingSelected) {
			return DownloadGroupName.SELECTED;
		}

		return null;
	}

	public static getGalleryLoaderStep(
		downloadManager: DownloadManagerState,
		driveList: DriveListState,
		galleryId: number,
		isDownloading: boolean,
		importantAssetTags: string[]
	): DownloadingAllStep | undefined {
		// TODO: When download all will not download shared assets anymore as requeted by Armand, we should ignore these assets in the count when importantAssetTags is disabled
		if (isDownloading) {
			return {
				step: LoaderStepName.DownloadingAll,
				itemsDownloaded: DownloadManagerUtils.getTotalItemsDownloaded(downloadManager, driveList, importantAssetTags),
				itemsToDownload: DownloadManagerUtils.getTotalItemsToDownload(driveList, galleryId, importantAssetTags),
			};
		}

		return;
	}

	public static isItemDownloading(downloadManager: DownloadManagerState, driveItemId: number) {
		return DownloadManagerUtils.isDownloadingAsset(downloadManager, driveItemId)
			|| (DownloadManagerUtils.isAssetDownloaded(downloadManager, driveItemId) && !DownloadManagerUtils.fetchedAllDependencies(downloadManager, driveItemId));
	}

	public static isItemUpdating(downloadManager: DownloadManagerState, driveItemId: number) {
		return DownloadManagerUtils.isUpdatingAsset(downloadManager, driveItemId)
			|| (DownloadManagerUtils.isAssetDownloaded(downloadManager, driveItemId) && DownloadManagerUtils.isUpdatingAnyDependency(downloadManager, driveItemId));
	}

	public static isItemDownloadingInProgress(downloadManager: DownloadManagerState, driveItemId: number) {
		const { downloadInProgressItems } = downloadManager;
		return downloadInProgressItems.some((id) => id === driveItemId);
	}

	public static isRequiredItem(downloadManager: DownloadManagerState, driveItemId: number): boolean {
		return downloadManager.items[driveItemId]
			&& downloadManager.items[driveItemId].mode === DownloadMode.Required;
	}

	public static isRequestedItem(downloadManager: DownloadManagerState, driveItemId: number): boolean {
		return downloadManager.items[driveItemId]
			&& downloadManager.items[driveItemId].mode === DownloadMode.Requested;
	}

	public static isPersistentItem(item: DownloadManagerItemState): boolean {
		return [DownloadMode.Requested, DownloadMode.Required].includes(item.mode);
	}

	public static isItemPersistentlyDownloaded(downloadManager: DownloadManagerState, driveItemId: number): boolean {
		const driveItemInDownloadManager = downloadManager.items[driveItemId];
		return driveItemInDownloadManager
			&& DownloadManagerUtils.isPersistentItem(driveItemInDownloadManager)
			&& DownloadManagerUtils.isItemDownloaded(downloadManager, driveItemId);
	}

	public static isItemDownloaded(downloadManager: DownloadManagerState, driveItemId: number): boolean {
		return DownloadManagerUtils.isAssetDownloaded(downloadManager, driveItemId)
			&& DownloadManagerUtils.isDependenciesDownloaded(downloadManager, driveItemId);
	}

	public static isLatestVersion = (serverVersion: number, downloadedVersion: number): boolean => downloadedVersion >= serverVersion;

	public static isDependencyOfPersistentDriveItem = (downloadManagerItems: { [k: number]: DownloadManagerItemState }, driveItemId: number, excludeArray: number[]): boolean => {
		const driveItemInDownloadManager = downloadManagerItems[driveItemId];
		const dependentsId = driveItemInDownloadManager.dependents;
		for (let i = 0; i < dependentsId.length; i++) {
			const dependentId = dependentsId[i];
			const isInExcludeArray = !excludeArray.includes(dependentId);
			if (isInExcludeArray) {
				const dependentInDownloadManager = downloadManagerItems[dependentId];
				if (DownloadManagerUtils.isPersistentItem(dependentInDownloadManager)) {
					return true;
				}

				if (DownloadManagerUtils.isDependencyOfPersistentDriveItem(downloadManagerItems, dependentId, [...excludeArray, ...dependentsId])) {
					return true;
				}
			}
		}
		return false;
	}

	public static hasItemsToDownload(downloadManager: DownloadManagerState, driveList: DriveListState, galleryInfos: GalleryInfosState, importantAssetTags: string[]): boolean {
		const { driveItems } = driveList;
		const driveItemsKeys = Object.keys(driveItems);
		let driveItemsIds: string[] = [];
		if (driveItemsKeys.length > 0) {
			const galleryId = galleryInfos.config.applicationGallery;
			driveItemsIds = driveItemsKeys
				.filter(DownloadManagerUtils.filterDownloadableItems(driveItems, galleryId))
				.filter(DownloadManagerUtils.filterImportantItems(driveItems, importantAssetTags));
		}

		const hasItemsToDownload = driveItemsIds.some(key => {
			const itemId = parseInt(key, 10);
			const isItemNotDownloaded = !DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, itemId);
			if (isItemNotDownloaded) {
				return true;
			}
			const downloadList = downloadManager.items;
			const needUpdate = !DownloadManagerUtils.isLatestVersion(driveItems[itemId].version, downloadList[itemId].version);

			return needUpdate;
		});

		return hasItemsToDownload;
	}

	public static getEnabledImportantTags(importantAssetsConfig?: Api.ImportantAssetsConfig) {
		if (!importantAssetsConfig || !importantAssetsConfig.enabled) {
			return [];
		}

		return importantAssetsConfig.tags;
	}

	public static filterImportantItems(
		driveListItems: DriveItemInfoMap,
		importantAssetTags: string[],
	): (driveItemId: string) => boolean {
		return (driveItemId: string) => {
			if (importantAssetTags.length === 0) {
				return true;
			}

			let driveItem = driveListItems[driveItemId];
			while (driveItem) {
				if (DownloadManagerUtils.isImportantItem(driveItem, importantAssetTags)) {
					return true;
				}

				const parentId = DriveItemUtils.getParentFolderId(driveItem);
				driveItem = driveListItems[parentId];
			}

			return false;
		};
	}

	public static hasDownloadErrors(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId]
			&& downloadManager.items[driveItemId].status === DownloadStatus.ERROR;
	}

	private static isImportantItem({ tags }: DriveItemInfo, importantAssetTags: string[]) {
		return tags.some(itemTag => importantAssetTags.includes(itemTag));
	}

	private static filterDownloadableItems(driveItems: DriveItemInfoMap, galleryId: number) {
		return (item: string) => {
			const driveItem = driveItems[item];
			const isInGallery = driveItem.isInGallery;
			const isDownloadableType = DriveItemUtils.isDownloadableItemType(driveItem);
			const { isTemplate } = driveItem;
			const isNotGalleryItself = parseInt(item, 10) !== galleryId;
			return isInGallery && isDownloadableType && isNotGalleryItself && !isTemplate;
		};
	}

	private static getTotalItemsDownloaded(downloadManager: DownloadManagerState, driveList: DriveListState, importantAssetTags: string[]): number {
		const { items } = downloadManager;
		const { driveItems } = driveList;
		const downloadedItemsIds = Object.keys(items)
			.filter(item => {
				return DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, parseInt(item, 10)) && driveItems[item];
			})
			.filter(DownloadManagerUtils.filterImportantItems(driveItems, importantAssetTags));

		return downloadedItemsIds.length;
	}

	private static getTotalItemsToDownload(driveList: DriveListState, galleryId: number, importantAssetTags: string[]): number {
		const { driveItems } = driveList;
		const downloadableItemsIds = Object.keys(driveItems)
			.filter(DownloadManagerUtils.filterDownloadableItems(driveItems, galleryId))
			.filter(DownloadManagerUtils.filterImportantItems(driveItems, importantAssetTags));
		return downloadableItemsIds.length;
	}

	private static getTotalDependenciesDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId].dependencies.reduce(DownloadManagerUtils.getTotalDependenciesDownloadedReducer(downloadManager), 0);
	}

	private static getTotalDependenciesDownloadedReducer(downloadManager: DownloadManagerState) {
		return (total: number, dependencyId: number): number => {

			if (downloadManager.items[dependencyId] && downloadManager.items[dependencyId].status === DownloadStatus.NATIVE_DOWNLOADED) {
				return total + 1;
			}
			return total;
		};
	}

	private static isDownloadingAsset(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId]
			&& [
				DownloadStatus.INIT,
				DownloadStatus.GETTING_DEPENDENCIES,
				DownloadStatus.NATIVE_DOWNLOADING,
				DownloadStatus.STARTING_DOWNLOAD,
				DownloadStatus.RESUMING_DOWNLOAD,
				DownloadStatus.STARTING_UPDATE,
				DownloadStatus.UPDATING,
			].includes(downloadManager.items[driveItemId].status);
	}

	private static isUpdatingAsset(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId]
			&& [
				DownloadStatus.STARTING_UPDATE,
				DownloadStatus.UPDATING,
			].includes(downloadManager.items[driveItemId].status);
	}

	private static isAssetDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		return downloadManager.items[driveItemId] && downloadManager.items[driveItemId].status === DownloadStatus.NATIVE_DOWNLOADED;
	}

	private static isDependenciesDownloaded(downloadManager: DownloadManagerState, driveItemId: number) {
		const itemFromDownloadManager = downloadManager.items[driveItemId];
		if (itemFromDownloadManager) {
			return itemFromDownloadManager.dependencies.every(dependencyId => DownloadManagerUtils.isAssetDownloaded(downloadManager, dependencyId));
		}
		return false;
	}

	private static fetchedAllDependencies(downloadManager: DownloadManagerState, driveItemId: number) {
		const itemFromDownloadManager = downloadManager.items[driveItemId];

		return itemFromDownloadManager.dependencies.every(dependencyId =>
			downloadManager.items[dependencyId] !== undefined &&
			DownloadManagerUtils.isAssetDownloaded(downloadManager, dependencyId)
		);
	}

	private static isUpdatingAnyDependency(downloadManager: DownloadManagerState, driveItemId: number) {
		const itemFromDownloadManager = downloadManager.items[driveItemId];

		return itemFromDownloadManager.dependencies.some(dependencyId =>
			downloadManager.items[dependencyId] !== undefined &&
			DownloadManagerUtils.isUpdatingAsset(downloadManager, dependencyId)
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/downloadManager.ts